import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin } from 'lucide-react';
import { useAvailableStates } from '@/hooks/use-available-states';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';

interface StateFilterProps {
  selected: string[];
  onChange: (states: string[]) => void;
  className?: string;
}

export function StateFilter({ selected, onChange, className = '' }: StateFilterProps) {
  const [open, setOpen] = useState(false);
  const availableStates = useAvailableStates();

  const toggleState = (id: string, checked: boolean) => {
    if (checked) {
      if (!selected.includes(id)) {
        onChange([...selected, id]);
      }
    } else {
      onChange(selected.filter((s) => s !== id));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`flex items-center ${className}`}>
          <MapPin className="h-4 w-4 mr-2" />
          States
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {selected.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2 space-y-2" align="start">
        <div className="flex justify-between text-xs px-1">
          <button
            className="text-primary hover:underline"
            onClick={() => onChange(availableStates.map((s) => s.id))}
          >
            Select All
          </button>
          <button className="text-primary hover:underline" onClick={() => onChange([])}>
            Clear All
          </button>
        </div>
        <ScrollArea className="h-60 pr-2">
          <div className="space-y-1">
            {availableStates.map((s) => (
              <label key={s.id} className="flex items-center space-x-2 text-sm">
                <Checkbox
                  id={`state-${s.id}`}
                  checked={selected.includes(s.id)}
                  onCheckedChange={(c) => toggleState(s.id, c === true)}
                />
                <span>{s.name}</span>
              </label>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
